import type { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import type { ExperienceTestTypeDto } from "@intelligems/ig-types/dtos/experiences";

import {
  camelToSnakeCase,
  officialCategoryName,
} from "@intelligems/ig-utils/common";

/**
 * Calculate product categories based on experience test types and category.
 */
export function calculateProductCategories(experience: {
  testTypes: ExperienceTestTypeDto;
  category: ExperienceCategory;
}): string[] {
  const productCategories: string[] = [];
  const category = officialCategoryName(experience.category, false);

  if (experience.testTypes.hasTestPricing) {
    productCategories.push(`price_${category}`);
  }
  if (experience.testTypes.hasTestShipping) {
    productCategories.push(`shipping_${category}`);
  }
  if (experience.testTypes.hasTestCampaign) {
    productCategories.push(`offer_${category}`);
  }
  if (experience.testTypes.hasTestCheckoutBlocks) {
    productCategories.push(`checkout_block_${category}`);
  }
  if (experience.testTypes.hasTestPostPurchase) {
    productCategories.push(`post_purchase_${category}`);
  }
  if (experience.testTypes.hasTestOnsiteUpsell) {
    productCategories.push(`onsite_upsell_${category}`);
  }
  if (
    experience.testTypes.hasTestContent ||
    experience.testTypes.hasTestContentTemplate ||
    experience.testTypes.hasTestContentUrl ||
    experience.testTypes.hasTestContentOnsite ||
    experience.testTypes.hasTestOnsiteInjections ||
    experience.testTypes.hasTestContentTheme ||
    experience.testTypes.hasTestContentAdvanced
  ) {
    productCategories.push(`content_${category}`);
  }

  return productCategories;
}

/**
 * Calculate modification types from test types.
 */
export function calculateModificationTypes(
  testTypes: ExperienceTestTypeDto
): string[] {
  const modificationTypes: string[] = [];

  if (testTypes.hasTestCampaign) {
    modificationTypes.push("offer");
  }
  if (testTypes.hasTestContentOnsite) {
    modificationTypes.push("onsite_edits");
  }
  if (testTypes.hasTestContentUrl) {
    modificationTypes.push("url_redirects");
  }
  if (testTypes.hasTestContentTheme) {
    modificationTypes.push("theme");
  }
  if (testTypes.hasTestContentTemplate) {
    modificationTypes.push("template");
  }
  if (testTypes.hasTestOnsiteInjections) {
    modificationTypes.push("css_js_injections");
  }
  if (testTypes.hasTestPricing) {
    modificationTypes.push("pricing");
  }
  if (testTypes.hasTestShipping) {
    modificationTypes.push("shipping");
  }
  if (testTypes.hasTestCheckoutBlocks) {
    modificationTypes.push("checkout_block");
  }
  if (testTypes.hasTestPostPurchase) {
    modificationTypes.push("post_purchase");
  }
  if (testTypes.hasTestOnsiteUpsell) {
    modificationTypes.push("onsite_upsell");
  }

  return modificationTypes;
}

/**
 * Structural type for variations — works with VariationDto, GenericVariationFe,
 * ExperienceListVariationDto, etc. without importing those types.
 */
export interface VariationWithComponents {
  userInterfaces?: Array<{
    widget?: { widgetType: string } | null;
  } | null> | null;
  checkoutBlocks?: Array<{ blockType?: string } | null> | null;
  [key: string]: unknown;
}

/**
 * Extract unique component types (widgets + checkout blocks) from variations.
 */
export function extractComponents(
  variations: readonly VariationWithComponents[]
): string[] {
  const components: string[] = [];

  for (const variation of variations) {
    for (const ui of variation.userInterfaces || []) {
      if (ui?.widget?.widgetType) {
        components.push(camelToSnakeCase(ui.widget.widgetType));
      }
    }
    for (const block of variation.checkoutBlocks || []) {
      if (block?.blockType) {
        components.push(camelToSnakeCase(block.blockType));
      }
    }
  }

  return Array.from(new Set(components));
}
